// Doctor Bank — a full page (route: /medical/doctor-bank), reached via the
// Medical Tracker's "Doctor Bank" button. Three tabs — Doctors, Hospitals,
// Diagnostic Centers — are all real, shared directories (models:
// backend/models/doctor.js / hospital.js / diagnosticCenter.js).
// Add/Edit/Delete is gated by the "Medical Check-in" Create permission —
// everyone who can open this page can always see every entry.
// The coach role is always view-only here regardless of that permission —
// coaches can browse all three tabs but never add/edit/delete entries.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import {
    Stethoscope, Building2, FlaskConical, Search, Plus, Pencil, Trash2, X, Phone, MapPin, ChevronDown,
    IdCard, ClipboardList, Calendar, Clock, Mail, Globe, Info, IndianRupee, FileText, Save, ArrowLeft,
    User, Briefcase, ImagePlus, Eye, MoreVertical, FlaskRound, TestTube,
} from 'lucide-react';
import {
    getAllDoctors, createDoctor, updateDoctor, deleteDoctor,
    getAllHospitals, createHospital, updateHospital, deleteHospital,
    getAllDiagnosticCenters, createDiagnosticCenter, updateDiagnosticCenter, deleteDiagnosticCenter,
} from '../../api';
import showToast from '../../utils/toast';
import { usePermission } from '../../hooks/usePermission';
import '../../components/dashboard/MedicalTracker.css';

// No "Other": a specialty that isn't listed is added with "+ Add New Specialty" instead.
const SPECIALTY_OPTIONS = [
    'General Physician', 'Pediatrician', 'Dermatologist', 'Orthopedic', 'ENT Specialist',
    'Cardiologist', 'Gynecologist', 'Neurologist', 'Ophthalmologist', 'Psychiatrist',
    'Dentist', 'Urologist', 'Gastroenterologist',
];
// The Specialty dropdown's last option: it opens a box for typing a new specialty.
const ADD_SPECIALTY = '__add_specialty__';
const specialtyKey = (value) => String(value || '').trim().toLowerCase();

const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// 6:00 AM through 10:00 PM in 30-minute steps, for the Days & Timings pickers.
const TIME_OPTIONS = (() => {
    const options = [];
    for (let minutes = 6 * 60; minutes <= 22 * 60; minutes += 30) {
        const h24 = Math.floor(minutes / 60);
        const m = minutes % 60;
        const period = h24 >= 12 ? 'PM' : 'AM';
        const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
        options.push(`${h12}:${String(m).padStart(2, '0')} ${period}`);
    }
    return options;
})();

const HOSPITAL_TYPE_OPTIONS = [
    'Multi-specialty', 'General Hospital', 'Clinic', 'Nursing Home',
    'Specialty Hospital', 'Government Hospital', 'Diagnostic Center', 'Other',
];

const CITY_OPTIONS = ['Pune', 'Mumbai', 'Pimpri-Chinchwad', 'Nashik', 'Other'];

const FACILITY_OPTIONS = [
    'Emergency (24x7)', 'ICU', 'Pediatrics', 'Surgery',
    'Maternity', 'Diagnostic Services', 'Pharmacy', 'Other',
];

const HOSPITAL_SPECIALTY_OPTIONS = [
    'General Medicine', 'Pediatrics', 'Orthopedics', 'Cardiology', 'ENT',
    'Dermatology', 'Gynecology', 'Neurology', 'Ophthalmology', 'Psychiatry',
    'Dentistry', 'Urology', 'Gastroenterology', 'Emergency Medicine', 'Other',
];

const emptyDoctorForm = {
    name: '', specialty: '', qualification: '', hospital: '', contactNumber: '',
    address: '', mapUrl: '', daysAvailable: [], timingsFrom: '', timingsTo: '',
    email: '', website: '', fees: '', notes: '',
};

// `knownSpecialties`: the specialties of the saved doctors, so one added for a doctor is offered
// for the next ones too.
function DoctorFormModal({ initialDoctor, onClose, onSaved, knownSpecialties = [] }) {
    const [form, setForm] = useState(initialDoctor
        ? {
            name: initialDoctor.name || '',
            specialty: initialDoctor.specialty || '',
            qualification: initialDoctor.qualification || '',
            hospital: initialDoctor.hospital || '',
            contactNumber: initialDoctor.contactNumber || '',
            address: initialDoctor.address || '',
            mapUrl: initialDoctor.mapUrl || '',
            daysAvailable: initialDoctor.daysAvailable || [],
            timingsFrom: initialDoctor.timingsFrom || '',
            timingsTo: initialDoctor.timingsTo || '',
            email: initialDoctor.email || '',
            website: initialDoctor.website || '',
            fees: initialDoctor.fees ?? '',
            notes: initialDoctor.notes || '',
        }
        : emptyDoctorForm);
    const [saving, setSaving] = useState(false);
    // '' = still on the "Search Hospital" placeholder, '__custom__' = manual
    // entry (the 3-field form), anything else = a registered Hospital's _id.
    // Editing an existing doctor always starts in Custom mode so its current
    // values show up exactly as they were, unchanged.
    const [hospitalPick, setHospitalPick] = useState(initialDoctor ? '__custom__' : '');
    const [hospitals, setHospitals] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const res = await getAllHospitals();
                setHospitals(res?.success ? (res.data || []) : []);
            } catch (error) {
                // the registered-hospital list is a convenience — Custom Hospital still works without it
            }
        })();
    }, []);

    const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

    // Same "does this hospital have enough info for a map link" rule the
    // Hospitals tab already uses — reused here so a doctor picked from the
    // registered list only gets a map link when one can actually be built.
    const getHospitalMapUrl = (hospital) => {
        if (!hospital.address && !hospital.city) return '';
        const query = [hospital.name, hospital.address, hospital.city].filter(Boolean).join(', ');
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
    };

    const handleHospitalPick = (value) => {
        setHospitalPick(value);
        if (value === '' || value === '__custom__') return;
        const hospital = hospitals.find((h) => h._id === value);
        if (!hospital) return;
        update({
            hospital: hospital.name,
            address: hospital.address || '',
            mapUrl: getHospitalMapUrl(hospital),
        });
    };

    // "Custom Hospital" is pinned at the top and visually set apart (bold,
    // colored, its own icon) via formatOptionLabel below, so it never reads
    // like just another registered hospital name in the search results.
    const hospitalSelectOptions = useMemo(() => ([
        { value: '__custom__', label: 'Custom Hospital', isCustom: true },
        ...hospitals.map((h) => ({ value: h._id, label: h.name })),
    ]), [hospitals]);

    const selectedHospitalOption = hospitalPick
        ? hospitalSelectOptions.find((o) => o.value === hospitalPick) || null
        : null;

    // "+ Add New Specialty": the box for typing one, and the ones added in this form.
    const [addingSpecialty, setAddingSpecialty] = useState(false);
    const [newSpecialty, setNewSpecialty] = useState('');
    const [addedSpecialties, setAddedSpecialties] = useState([]);

    // The fixed list, then the specialties added before (saved on other doctors; "Other" left out),
    // then the ones added here - each once. Editing a doctor whose specialty is in none of them (an
    // older one, or "Other") shouldn't silently blank the field out — keep its current value
    // selectable, first.
    const specialtyOptions = useMemo(() => {
        const seen = new Set();
        const listed = [
            ...SPECIALTY_OPTIONS,
            ...knownSpecialties.filter((s) => specialtyKey(s) !== 'other').sort((a, b) => a.localeCompare(b)),
            ...addedSpecialties,
        ].filter((s) => {
            const key = specialtyKey(s);
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
        });
        return form.specialty && !listed.includes(form.specialty) ? [form.specialty, ...listed] : listed;
    }, [knownSpecialties, addedSpecialties, form.specialty]);

    const cancelNewSpecialty = () => {
        setAddingSpecialty(false);
        setNewSpecialty('');
    };
    // Adds the typed specialty and selects it; one already listed (any case) is just selected.
    const addNewSpecialty = () => {
        const name = newSpecialty.trim().replace(/\s+/g, ' ');
        if (name.length < 2) { showToast('Specialty name must be at least 2 characters', 'error'); return; }
        if (specialtyKey(name) === 'other') { showToast('Please type the name of the specialty', 'error'); return; }
        const existing = specialtyOptions.find((s) => specialtyKey(s) === specialtyKey(name));
        if (!existing) setAddedSpecialties((prev) => [...prev, name]);
        update({ specialty: existing || name });
        cancelNewSpecialty();
    };

    const toggleDay = (day) => {
        update({
            daysAvailable: form.daysAvailable.includes(day)
                ? form.daysAvailable.filter((d) => d !== day)
                : [...form.daysAvailable, day],
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const name = form.name.trim();
        if (!name || name.length < 3) { showToast('Doctor name must be at least 3 characters', 'error'); return; }
        if (addingSpecialty && newSpecialty.trim()) { showToast('Click "Add" to add the new specialty first', 'error'); return; }
        if (!form.specialty) { showToast('Please select a specialty', 'error'); return; }
        if (!form.hospital.trim()) { showToast('Hospital / Clinic name is required', 'error'); return; }
        if (form.daysAvailable.length === 0) { showToast('Please select at least one available day', 'error'); return; }
        if (!form.timingsFrom || !form.timingsTo) { showToast('Please select available timings', 'error'); return; }
        if (!form.contactNumber.trim()) { showToast('Phone number is required', 'error'); return; }

        setSaving(true);
        try {
            const payload = {
                name,
                specialty: form.specialty,
                qualification: form.qualification.trim(),
                hospital: form.hospital.trim(),
                contactNumber: form.contactNumber.trim(),
                address: form.address.trim(),
                mapUrl: form.mapUrl.trim(),
                daysAvailable: form.daysAvailable,
                timingsFrom: form.timingsFrom,
                timingsTo: form.timingsTo,
                email: form.email.trim(),
                website: form.website.trim(),
                fees: form.fees === '' ? '' : Number(form.fees),
                notes: form.notes.trim(),
            };
            const response = initialDoctor ? await updateDoctor(initialDoctor._id, payload) : await createDoctor(payload);
            if (response?.success) {
                showToast(initialDoctor ? 'Doctor updated' : 'Doctor added to data bank', 'success');
                onSaved();
                onClose();
            } else {
                showToast(response?.message || 'Failed to save doctor', 'error');
            }
        } catch (error) {
            showToast(error?.response?.data?.message || 'Failed to save doctor', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="medical-modal-overlay" style={{ zIndex: 60 }}>
            <div className="medical-modal doctor-form-modal">
                <div className="doctor-form-header">
                    <div className="doctor-form-header-icon" aria-hidden="true"><Stethoscope size={26} /></div>
                    <div className="doctor-form-header-text">
                        <h3>{initialDoctor ? 'Edit Doctor' : 'Add Doctor'}</h3>
                        <p>Enter doctor details to help children get the right medical care.</p>
                    </div>
                    <button type="button" className="medical-modal-close" onClick={onClose} aria-label="Close"><X size={20} /></button>
                </div>

                <form onSubmit={handleSubmit} className="doctor-form">
                    <div className="doctor-form-section">
                        <div className="doctor-form-section-title"><IdCard size={16} /> Basic Information</div>
                        <div className="doctor-form-row">
                            <div className="medical-form-group">
                                <label>Doctor Name <span className="required">*</span></label>
                                <input type="text" value={form.name} onChange={(e) => update({ name: e.target.value })} placeholder="Enter doctor name" required />
                            </div>
                            <div className="medical-form-group">
                                <label>Specialty <span className="required">*</span></label>
                                <div className="medical-filter" style={{ width: '100%' }}>
                                    <select
                                        className="medical-filter-select doctor-form-select"
                                        aria-label="Specialty"
                                        value={form.specialty}
                                        onChange={(e) => {
                                            if (e.target.value === ADD_SPECIALTY) setAddingSpecialty(true);
                                            else update({ specialty: e.target.value });
                                        }}
                                        required
                                    >
                                        <option value="">Select specialty</option>
                                        {specialtyOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                                        <option value={ADD_SPECIALTY}>+ Add New Specialty</option>
                                    </select>
                                    <ChevronDown size={14} className="medical-filter-chevron" aria-hidden="true" />
                                </div>
                                {addingSpecialty && (
                                    <div className="doctor-specialty-add">
                                        <input
                                            type="text"
                                            value={newSpecialty}
                                            onChange={(e) => setNewSpecialty(e.target.value)}
                                            onKeyDown={(e) => {
                                                // Enter adds it (instead of saving the doctor); Escape closes the box.
                                                if (e.key === 'Enter') { e.preventDefault(); addNewSpecialty(); }
                                                if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); cancelNewSpecialty(); }
                                            }}
                                            placeholder="New specialty, e.g. Pulmonologist"
                                            aria-label="New specialty"
                                            maxLength={60}
                                            autoFocus
                                        />
                                        <button type="button" className="doctor-specialty-add-save" onClick={addNewSpecialty}>Add</button>
                                        <button type="button" className="doctor-specialty-add-cancel" onClick={cancelNewSpecialty} aria-label="Cancel new specialty"><X size={14} /></button>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="doctor-form-row">
                            <div className="medical-form-group">
                                <label>Qualification (Optional)</label>
                                <input type="text" value={form.qualification} onChange={(e) => update({ qualification: e.target.value })} placeholder="e.g. MBBS, MS" />
                            </div>
                        </div>
                        <div className="doctor-form-row">
                            <div className="medical-form-group" style={{ flexBasis: '100%' }}>
                                <label>Hospital / Clinic <span className="required">*</span></label>
                                <Select
                                    classNamePrefix="doctor-hospital-select"
                                    options={hospitalSelectOptions}
                                    value={selectedHospitalOption}
                                    onChange={(option) => handleHospitalPick(option ? option.value : '')}
                                    placeholder="Search Hospital"
                                    isClearable
                                    formatOptionLabel={(option) => option.isCustom ? (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 700, color: '#0634d8' }}>
                                            <Plus size={13} aria-hidden="true" /> {option.label}
                                        </span>
                                    ) : option.label}
                                    styles={{
                                        control: (base, state) => ({
                                            ...base,
                                            minHeight: '38px',
                                            borderRadius: 8,
                                            borderColor: state.isFocused ? '#6d7cf2' : '#d7e4fb',
                                            boxShadow: 'none',
                                            fontSize: 13,
                                        }),
                                        placeholder: (base) => ({
                                            ...base,
                                            color: '#0634d8',
                                            fontWeight: 600,
                                        }),
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        menu: (base) => ({ ...base, zIndex: 9999 }),
                                    }}
                                    menuPortalTarget={document.body}
                                />
                                {/* Keeps native `required` validation tied to an actual selection */}
                                <input
                                    type="text"
                                    value={hospitalPick}
                                    onChange={() => {}}
                                    required
                                    style={{ display: 'none' }}
                                    tabIndex={-1}
                                />
                            </div>
                        </div>

                        {hospitalPick === '__custom__' && (
                            <div className="doctor-form-row">
                                <div className="medical-form-group" style={{ flexBasis: '100%' }}>
                                    <label>Hospital / Clinic Name <span className="required">*</span></label>
                                    <input type="text" value={form.hospital} onChange={(e) => update({ hospital: e.target.value })} placeholder="Enter hospital or clinic name" required />
                                </div>
                            </div>
                        )}

                        {hospitalPick === '__custom__' && (
                            <div className="doctor-form-row">
                                <div className="medical-form-group">
                                    <label>Address (Optional)</label>
                                    <div className="doctor-form-icon-field">
                                        <MapPin size={15} className="doctor-form-icon" />
                                        <textarea rows={2} maxLength={250} value={form.address} onChange={(e) => update({ address: e.target.value })} placeholder="Enter complete address" />
                                    </div>
                                    <div className="doctor-form-char-count">{form.address.length}/250</div>
                                </div>
                                <div className="medical-form-group">
                                    <label>Google Map URL (Optional)</label>
                                    <div className="doctor-form-icon-field">
                                        <Globe size={15} className="doctor-form-icon" />
                                        <input type="text" value={form.mapUrl} onChange={(e) => update({ mapUrl: e.target.value })} placeholder="Paste the Google Maps link for this location" />
                                    </div>
                                    <small className="medical-form-hint">Used for the &quot;View on Map&quot; link.</small>
                                </div>
                            </div>
                        )}

                        {hospitalPick !== '' && hospitalPick !== '__custom__' && (
                            <div className="doctor-form-row">
                                <div className="medical-form-group">
                                    <label>Address <span className="doctor-form-optional">(fetched from hospital)</span></label>
                                    <div className="doctor-form-icon-field">
                                        <MapPin size={15} className="doctor-form-icon" />
                                        <textarea rows={2} value={form.address} readOnly placeholder="This hospital has no address on file" />
                                    </div>
                                </div>
                                <div className="medical-form-group">
                                    <label>Map Link</label>
                                    <p className="medical-form-hint" style={{ marginTop: 9 }}>
                                        {form.mapUrl
                                            ? 'A "View on Map" link will show for this doctor.'
                                            : 'This hospital has no location on file, so no "View on Map" link will show.'}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="doctor-form-section">
                        <div className="doctor-form-section-title"><ClipboardList size={16} /> Clinical Details</div>
                        <div className="doctor-form-row">
                            <div className="medical-form-group">
                                <label>Days &amp; Timings <span className="required">*</span></label>
                                <div className="doctor-form-days-label"><Calendar size={13} /> Available Days</div>
                                <div className="doctor-form-days">
                                    {DAYS_OF_WEEK.map((day) => (
                                        <button
                                            key={day}
                                            type="button"
                                            className={`doctor-form-day-pill ${form.daysAvailable.includes(day) ? 'active' : ''}`}
                                            onClick={() => toggleDay(day)}
                                        >
                                            {day}
                                        </button>
                                    ))}
                                </div>
                                <div className="doctor-form-timings">
                                    <div className="medical-form-group">
                                        <label>From <span className="required">*</span></label>
                                        <div className="medical-filter" style={{ width: '100%' }}>
                                            <Clock size={13} className="medical-filter-chevron doctor-form-select-icon-left" aria-hidden="true" />
                                            <select className="medical-filter-select doctor-form-select doctor-form-select-with-left-icon" value={form.timingsFrom} onChange={(e) => update({ timingsFrom: e.target.value })}>
                                                <option value="">Select time</option>
                                                {TIME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                                            </select>
                                            <ChevronDown size={14} className="medical-filter-chevron" aria-hidden="true" />
                                        </div>
                                    </div>
                                    <div className="medical-form-group">
                                        <label>To <span className="required">*</span></label>
                                        <div className="medical-filter" style={{ width: '100%' }}>
                                            <Clock size={13} className="medical-filter-chevron doctor-form-select-icon-left" aria-hidden="true" />
                                            <select className="medical-filter-select doctor-form-select doctor-form-select-with-left-icon" value={form.timingsTo} onChange={(e) => update({ timingsTo: e.target.value })}>
                                                <option value="">Select time</option>
                                                {TIME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                                            </select>
                                            <ChevronDown size={14} className="medical-filter-chevron" aria-hidden="true" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="doctor-form-section">
                        <div className="doctor-form-section-title"><Phone size={16} /> Contact Information</div>
                        <div className="doctor-form-row three">
                            <div className="medical-form-group">
                                <label>Phone Number <span className="required">*</span></label>
                                <div className="doctor-form-icon-field">
                                    <Phone size={15} className="doctor-form-icon" />
                                    <input type="tel" value={form.contactNumber} onChange={(e) => update({ contactNumber: e.target.value })} placeholder="Enter phone number" required />
                                </div>
                            </div>
                            <div className="medical-form-group">
                                <label>Email (Optional)</label>
                                <div className="doctor-form-icon-field">
                                    <Mail size={15} className="doctor-form-icon" />
                                    <input type="email" value={form.email} onChange={(e) => update({ email: e.target.value })} placeholder="Enter email address" />
                                </div>
                            </div>
                            <div className="medical-form-group">
                                <label>Website (Optional)</label>
                                <div className="doctor-form-icon-field">
                                    <Globe size={15} className="doctor-form-icon" />
                                    <input type="text" value={form.website} onChange={(e) => update({ website: e.target.value })} placeholder="Enter website (if any)" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="doctor-form-section">
                        <div className="doctor-form-section-title"><Info size={16} /> Additional Information <span className="doctor-form-optional">(Optional)</span></div>
                        <div className="doctor-form-row">
                            <div className="medical-form-group">
                                <label>Fees / Consultation Charges (Optional)</label>
                                <div className="doctor-form-icon-field">
                                    <IndianRupee size={15} className="doctor-form-icon" />
                                    <input type="number" min="0" value={form.fees} onChange={(e) => update({ fees: e.target.value })} placeholder="Enter consultation fee (e.g. 500)" />
                                </div>
                            </div>
                            <div className="medical-form-group">
                                <label>Notes (Optional)</label>
                                <div className="doctor-form-icon-field">
                                    <FileText size={15} className="doctor-form-icon" />
                                    <textarea rows={2} maxLength={250} value={form.notes} onChange={(e) => update({ notes: e.target.value })} placeholder="Any additional notes (e.g. languages, wheelchair access, etc.)" />
                                </div>
                                <div className="doctor-form-char-count">{form.notes.length}/250</div>
                            </div>
                        </div>
                    </div>

                    <div className="medical-form-footer">
                        <button type="button" className="ghost" onClick={onClose} disabled={saving}>Cancel</button>
                        <button type="submit" className="doctor-form-save" disabled={saving}>
                            <Save size={15} /> {saving ? 'Saving...' : 'Save Doctor'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

const DOCTORS_PER_PAGE = 10;

// Column headings shown once above a list instead of in every row. The rows keep their own
// labels, hidden on wide screens (screen readers still read them) and shown again on narrow
// screens, where the rows wrap and can't line up under a heading.
const ListHead = ({ columns, className = '' }) => (
    <div className={`doctor-list-head ${className}`.trim()} aria-hidden="true">
        {columns.map((label, i) => <div key={i}>{label}</div>)}
    </div>
);

// Mon..Sun in that order shown as "Mon - Sat" when the picked days are a
// consecutive run; otherwise just comma-joined in week order.
const formatDayRange = (days) => {
    if (!Array.isArray(days) || days.length === 0) return null;
    const ordered = DAYS_OF_WEEK.filter((d) => days.includes(d));
    if (ordered.length === 0) return null;
    const indices = ordered.map((d) => DAYS_OF_WEEK.indexOf(d));
    const isConsecutive = indices.every((idx, i) => i === 0 || idx === indices[i - 1] + 1);
    if (isConsecutive && ordered.length > 1) return `${ordered[0]} - ${ordered[ordered.length - 1]}`;
    return ordered.join(', ');
};

// "View on Map" only ever opens a link the coach actually supplied — no
// guessed search query when a doctor doesn't have one.
const getMapUrl = (doctor) => {
    if (!doctor.mapUrl) return null;
    return /^https?:\/\//i.test(doctor.mapUrl) ? doctor.mapUrl : `https://${doctor.mapUrl}`;
};

// Read-only detail view (the "View Details" button).
function DoctorDetailsModal({ doctor, onClose }) {
    const mapUrl = getMapUrl(doctor);
    return (
        <div className="medical-modal-overlay" style={{ zIndex: 60 }}>
            <div className="medical-modal medical-view-modal">
                <div className="medical-modal-header">
                    <h3>{doctor.name}</h3>
                    <button type="button" className="medical-modal-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
                </div>
                <div className="medical-view-body">
                    <div className="medical-view-grid">
                        <div><span>Specialty</span><strong>{doctor.specialty || '-'}</strong></div>
                        <div><span>Qualification</span><strong>{doctor.qualification || '-'}</strong></div>
                        <div><span>Hospital / Clinic</span><strong>{doctor.hospital || '-'}</strong></div>
                        <div><span>Phone</span><strong>{doctor.contactNumber || '-'}</strong></div>
                    </div>
                    <div className="medical-view-block">
                        <span>Common Symptoms</span>
                        <p>{doctor.commonSymptoms || '-'}</p>
                    </div>
                    <div className="medical-view-block">
                        <span>Address</span>
                        <p>
                            {doctor.address || (!mapUrl && '-')}
                            {mapUrl && <>{doctor.address ? <> &middot; </> : ''}<a href={mapUrl} target="_blank" rel="noopener noreferrer">View on Map</a></>}
                        </p>
                    </div>
                    <div className="medical-view-block">
                        <span>Days &amp; Timings</span>
                        <p>{formatDayRange(doctor.daysAvailable) || '-'}{doctor.timingsFrom ? ` · ${doctor.timingsFrom} - ${doctor.timingsTo || ''}` : ''}</p>
                    </div>
                    {(doctor.email || doctor.website) && (
                        <div className="medical-view-block">
                            <span>Contact</span>
                            <p>{[doctor.email, doctor.website].filter(Boolean).join(' · ')}</p>
                        </div>
                    )}
                    {doctor.fees !== undefined && doctor.fees !== null && doctor.fees !== '' && (
                        <div className="medical-view-block">
                            <span>Consultation Fee</span>
                            <p>&#8377;{doctor.fees}</p>
                        </div>
                    )}
                    {doctor.notes && (
                        <div className="medical-view-block">
                            <span>Notes</span>
                            <p>{doctor.notes}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function DoctorsTab({ canManage, onCountChange }) {
    const [doctors, setDoctors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [specialtyFilter, setSpecialtyFilter] = useState('all');
    const [areaFilter, setAreaFilter] = useState('all');
    const [dayFilter, setDayFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [formOpen, setFormOpen] = useState(false);
    const [editingDoctor, setEditingDoctor] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [viewingDoctor, setViewingDoctor] = useState(null);

    const fetchDoctors = async () => {
        setLoading(true);
        try {
            const res = await getAllDoctors();
            const list = res?.success ? (res.data || []) : [];
            setDoctors(list);
            onCountChange?.(list.length);
        } catch (error) {
            showToast('Failed to load doctors', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchDoctors(); }, []);
    useEffect(() => { setCurrentPage(1); }, [search, specialtyFilter, areaFilter, dayFilter]);

    const specialties = useMemo(
        () => Array.from(new Set(doctors.map((d) => d.specialty).filter(Boolean))).sort(),
        [doctors]
    );
    const areas = useMemo(
        () => Array.from(new Set(doctors.map((d) => d.address).filter(Boolean))).sort(),
        [doctors]
    );

    const filtered = doctors.filter((d) => {
        if (specialtyFilter !== 'all' && d.specialty !== specialtyFilter) return false;
        if (areaFilter !== 'all' && d.address !== areaFilter) return false;
        if (dayFilter !== 'all' && !(d.daysAvailable || []).includes(dayFilter)) return false;
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return (d.name || '').toLowerCase().includes(q)
            || (d.specialty || '').toLowerCase().includes(q)
            || (d.hospital || '').toLowerCase().includes(q)
            || (d.commonSymptoms || '').toLowerCase().includes(q);
    });

    const hasActiveFilters = search || specialtyFilter !== 'all' || areaFilter !== 'all' || dayFilter !== 'all';
    const clearFilters = () => { setSearch(''); setSpecialtyFilter('all'); setAreaFilter('all'); setDayFilter('all'); };

    const totalPages = Math.max(1, Math.ceil(filtered.length / DOCTORS_PER_PAGE));
    const pageSafe = Math.min(currentPage, totalPages);
    const paginated = filtered.slice((pageSafe - 1) * DOCTORS_PER_PAGE, pageSafe * DOCTORS_PER_PAGE);

    const handleDelete = async (id) => {
        try {
            const res = await deleteDoctor(id);
            if (res?.success) {
                showToast('Doctor removed', 'success');
                setDeletingId(null);
                fetchDoctors();
            } else {
                showToast(res?.message || 'Failed to delete doctor', 'error');
            }
        } catch (error) {
            showToast(error?.response?.data?.message || 'Failed to delete doctor', 'error');
        }
    };

    return (
        <div>
            <div className="doctor-list-header">
                <div>
                    <h4>Doctors ({doctors.length})</h4>
                    <p>Find trusted doctors for children&apos;s health and general care.</p>
                </div>
                {canManage && (
                    <button type="button" className="medical-add-btn" onClick={() => { setEditingDoctor(null); setFormOpen(true); }}>
                        <Plus size={16} /> Add Doctor
                    </button>
                )}
            </div>

            <div className="medical-bank-toolbar">
                <div className="medical-search">
                    <Search size={16} className="medical-search-icon" aria-hidden="true" />
                    <input type="text" placeholder="Search by doctor name, specialty or symptom..." value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <div className="medical-filter">
                    <select className="medical-filter-select" value={specialtyFilter} onChange={(e) => setSpecialtyFilter(e.target.value)}>
                        <option value="all">All Specialties</option>
                        {specialties.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <ChevronDown size={14} className="medical-filter-chevron" aria-hidden="true" />
                </div>
                <div className="medical-filter">
                    <select className="medical-filter-select" value={areaFilter} onChange={(e) => setAreaFilter(e.target.value)}>
                        <option value="all">All Areas</option>
                        {areas.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                    <ChevronDown size={14} className="medical-filter-chevron" aria-hidden="true" />
                </div>
                <div className="medical-filter">
                    <select className="medical-filter-select" value={dayFilter} onChange={(e) => setDayFilter(e.target.value)}>
                        <option value="all">All Days</option>
                        {DAYS_OF_WEEK.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <ChevronDown size={14} className="medical-filter-chevron" aria-hidden="true" />
                </div>
                {hasActiveFilters && (
                    <button type="button" className="medical-clear-filters" onClick={clearFilters}>Clear Filters</button>
                )}
            </div>

            {loading ? (
                <div className="medical-empty">Loading doctors...</div>
            ) : filtered.length === 0 ? (
                <div className="medical-empty">No doctors match your search.</div>
            ) : (
                <div className="doctor-list-table">
                    <ListHead columns={['Doctor', 'Clinic / Hospital', 'Days & Timings', 'Contact', '']} />
                    {paginated.map((doctor) => {
                        const dayRange = formatDayRange(doctor.daysAvailable);
                        const mapUrl = getMapUrl(doctor);
                        return (
                            <div key={doctor._id} className="doctor-list-row">
                                <div className="doctor-list-cell doctor-list-cell-identity">
                                    <div className="medical-doctor-avatar" aria-hidden="true">{(doctor.name || '?').replace(/^Dr\.?\s*/i, '').charAt(0).toUpperCase()}</div>
                                    <div>
                                        <strong>{doctor.name}</strong>
                                        {(doctor.qualification || doctor.specialty) && (
                                            <div className="doctor-list-qualification">
                                                {doctor.qualification}{doctor.qualification && doctor.specialty ? ` (${doctor.specialty})` : (doctor.specialty || '')}
                                            </div>
                                        )}
                                        {doctor.specialty && <span className="medical-doctor-tag">{doctor.specialty}</span>}
                                    </div>
                                </div>
                                <div className="doctor-list-cell">
                                    <div className="doctor-list-cell-label">Clinic / Hospital</div>
                                    {doctor.hospital && <div className="doctor-list-hospital">{doctor.hospital}</div>}
                                    {doctor.address && <div className="doctor-list-address"><MapPin size={12} /> {doctor.address}</div>}
                                </div>
                                <div className="doctor-list-cell">
                                    <div className="doctor-list-cell-label">Days &amp; Timings</div>
                                    {dayRange && <div className="doctor-list-days"><Calendar size={12} /> {dayRange}</div>}
                                    {doctor.timingsFrom && <div className="doctor-list-timings">{doctor.timingsFrom} - {doctor.timingsTo || ''}</div>}
                                    {!dayRange && !doctor.timingsFrom && '-'}
                                </div>
                                <div className="doctor-list-cell doctor-list-cell-contact">
                                    {doctor.contactNumber && <a className="doctor-list-phone" href={`tel:${doctor.contactNumber}`}><Phone size={13} /> {doctor.contactNumber}</a>}
                                    {mapUrl && <a className="doctor-list-map" href={mapUrl} target="_blank" rel="noopener noreferrer"><MapPin size={13} /> View on Map</a>}
                                </div>
                                <div className="doctor-list-cell doctor-list-cell-actions">
                                    <button type="button" className="doctor-list-view-btn" onClick={() => setViewingDoctor(doctor)}>View Details</button>
                                    {canManage && (
                                        <div className="medical-doctor-actions">
                                            <button type="button" onClick={() => { setEditingDoctor(doctor); setFormOpen(true); }} aria-label="Edit doctor" title="Edit"><Pencil size={14} /></button>
                                            <button type="button" className="danger" onClick={() => setDeletingId(doctor._id)} aria-label="Delete doctor" title="Delete"><Trash2 size={14} /></button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {filtered.length > 0 && (
                <div className="medical-footer">
                    <div className="medical-footer-info">
                        {`Showing ${(pageSafe - 1) * DOCTORS_PER_PAGE + 1} to ${Math.min(pageSafe * DOCTORS_PER_PAGE, filtered.length)} of ${filtered.length} doctors`}
                    </div>
                    {totalPages > 1 && (
                        <div className="medical-pagination">
                            <button disabled={pageSafe === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>&lsaquo;</button>
                            {Array.from({ length: totalPages }).map((_, i) => (
                                <button key={i} className={pageSafe === i + 1 ? 'active' : ''} onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                            ))}
                            <button disabled={pageSafe === totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}>&rsaquo;</button>
                        </div>
                    )}
                </div>
            )}

            {formOpen && (
                <DoctorFormModal
                    initialDoctor={editingDoctor}
                    knownSpecialties={specialties}
                    onClose={() => { setFormOpen(false); setEditingDoctor(null); }}
                    onSaved={fetchDoctors}
                />
            )}

            {viewingDoctor && (
                <DoctorDetailsModal doctor={viewingDoctor} onClose={() => setViewingDoctor(null)} />
            )}

            {deletingId && (
                <div className="medical-modal-overlay" style={{ zIndex: 60 }}>
                    <div className="medical-modal medical-confirm-modal">
                        <h3>Delete doctor?</h3>
                        <p>This removes the doctor from the shared data bank.</p>
                        <div className="medical-confirm-actions">
                            <button type="button" className="ghost" onClick={() => setDeletingId(null)}>Cancel</button>
                            <button type="button" className="danger" onClick={() => handleDelete(deletingId)}>Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const emptyHospitalForm = {
    name: '', type: '', contactPerson: '', designation: '',
    address: '', city: 'Pune', contactNumber: '', email: '', website: '',
    facilities: [], specialties: [], daysAvailable: [], timingsFrom: '', timingsTo: '',
    open24Hours: false, additionalInfo: '', doctors: [],
};

// Custom react-select Option renderer so the multi-select doctor picker
// shows an actual checkbox next to each name, matching how every other
// multi-pick field in this form (facilities, specialties, days) looks.
function CheckboxOption(props) {
    const { innerRef, innerProps, isSelected, isFocused, label } = props;
    return (
        <div
            ref={innerRef}
            {...innerProps}
            className={`doctor-multiselect-option ${isFocused ? 'is-focused' : ''}`}
        >
            <input type="checkbox" checked={isSelected} readOnly />
            <span>{label}</span>
        </div>
    );
}

function HospitalFormModal({ initialHospital, onClose, onSaved }) {
    const [form, setForm] = useState(initialHospital
        ? {
            name: initialHospital.name || '',
            type: initialHospital.type || '',
            contactPerson: initialHospital.contactPerson || '',
            designation: initialHospital.designation || '',
            address: initialHospital.address || '',
            city: initialHospital.city || 'Pune',
            contactNumber: initialHospital.contactNumber || '',
            email: initialHospital.email || '',
            website: initialHospital.website || '',
            facilities: initialHospital.facilities || [],
            specialties: initialHospital.specialties
                ? initialHospital.specialties.split(',').map((s) => s.trim()).filter(Boolean)
                : [],
            daysAvailable: initialHospital.daysAvailable || [],
            timingsFrom: initialHospital.timingsFrom || '',
            timingsTo: initialHospital.timingsTo || '',
            open24Hours: !!initialHospital.open24Hours,
            additionalInfo: initialHospital.additionalInfo || '',
            doctors: (initialHospital.doctors || []).map((d) => (typeof d === 'object' ? d._id : d)),
        }
        : emptyHospitalForm);
    const [existingImages] = useState(initialHospital?.images || []);
    const [newImages, setNewImages] = useState([]);
    const [saving, setSaving] = useState(false);
    const [doctorOptions, setDoctorOptions] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const res = await getAllDoctors();
                setDoctorOptions(res?.success ? (res.data || []) : []);
            } catch (error) {
                // the doctors list is a convenience field — saving the hospital still works without it
            }
        })();
    }, []);

    const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

    const typeOptions = form.type && !HOSPITAL_TYPE_OPTIONS.includes(form.type)
        ? [form.type, ...HOSPITAL_TYPE_OPTIONS]
        : HOSPITAL_TYPE_OPTIONS;
    const cityOptions = form.city && !CITY_OPTIONS.includes(form.city)
        ? [form.city, ...CITY_OPTIONS]
        : CITY_OPTIONS;

    const toggleDay = (day) => {
        update({
            daysAvailable: form.daysAvailable.includes(day)
                ? form.daysAvailable.filter((d) => d !== day)
                : [...form.daysAvailable, day],
        });
    };
    const toggleFacility = (facility) => {
        update({
            facilities: form.facilities.includes(facility)
                ? form.facilities.filter((f) => f !== facility)
                : [...form.facilities, facility],
        });
    };
    const toggleSpecialty = (specialty) => {
        update({
            specialties: form.specialties.includes(specialty)
                ? form.specialties.filter((s) => s !== specialty)
                : [...form.specialties, specialty],
        });
    };

    const doctorSelectOptions = useMemo(() => (
        doctorOptions.map((d) => ({ value: d._id, label: d.specialty ? `${d.name} — ${d.specialty}` : d.name }))
    ), [doctorOptions]);
    const selectedDoctorOptions = doctorSelectOptions.filter((o) => form.doctors.includes(o.value));

    const addImageFiles = (fileList) => {
        const files = Array.from(fileList || []).filter((f) => f.type.startsWith('image/'));
        if (files.length === 0) return;
        setNewImages((prev) => [...prev, ...files].slice(0, 6 - existingImages.length));
    };
    const removeNewImage = (index) => setNewImages((prev) => prev.filter((_, i) => i !== index));

    const handleSubmit = async (e) => {
        e.preventDefault();
        const name = form.name.trim();
        if (!name || name.length < 3) { showToast('Hospital name must be at least 3 characters', 'error'); return; }
        if (!form.type) { showToast('Please select a hospital type', 'error'); return; }
        if (!form.address.trim()) { showToast('Complete address is required', 'error'); return; }
        if (!form.city) { showToast('Please select an area / city', 'error'); return; }
        if (!form.contactNumber.trim()) { showToast('Phone number is required', 'error'); return; }
        if (form.facilities.length === 0) { showToast('Please select at least one facility', 'error'); return; }
        if (form.specialties.length === 0) { showToast('Please select at least one specialty / department', 'error'); return; }
        if (form.daysAvailable.length === 0) { showToast('Please select at least one open day', 'error'); return; }
        if (!form.open24Hours && (!form.timingsFrom || !form.timingsTo)) {
            showToast('Please select opening and closing time, or mark Open 24 Hours', 'error');
            return;
        }

        setSaving(true);
        try {
            const fd = new FormData();
            fd.append('name', name);
            fd.append('type', form.type);
            fd.append('contactPerson', form.contactPerson.trim());
            fd.append('designation', form.designation.trim());
            fd.append('address', form.address.trim());
            fd.append('city', form.city);
            fd.append('contactNumber', form.contactNumber.trim());
            fd.append('email', form.email.trim());
            fd.append('website', form.website.trim());
            fd.append('facilities', JSON.stringify(form.facilities));
            fd.append('specialties', form.specialties.join(', '));
            fd.append('daysAvailable', JSON.stringify(form.daysAvailable));
            fd.append('timingsFrom', form.open24Hours ? '' : form.timingsFrom);
            fd.append('timingsTo', form.open24Hours ? '' : form.timingsTo);
            fd.append('open24Hours', String(form.open24Hours));
            fd.append('additionalInfo', form.additionalInfo.trim());
            fd.append('doctors', JSON.stringify(form.doctors));
            newImages.forEach((file) => fd.append('images', file));

            const response = initialHospital ? await updateHospital(initialHospital._id, fd) : await createHospital(fd);
            if (response?.success) {
                showToast(initialHospital ? 'Hospital updated' : 'Hospital added to data bank', 'success');
                onSaved();
                onClose();
            } else {
                showToast(response?.message || 'Failed to save hospital', 'error');
            }
        } catch (error) {
            showToast(error?.response?.data?.message || 'Failed to save hospital', 'error');
        } finally {
            setSaving(false);
        }
    };

    const previewImage = newImages[0] ? URL.createObjectURL(newImages[0]) : existingImages[0]?.fileUrl;

    return (
        <div className="medical-modal-overlay" style={{ zIndex: 60 }}>
            <div className="medical-modal doctor-form-modal hospital-form-modal">
                <div className="doctor-form-header">
                    <div className="doctor-form-header-icon" aria-hidden="true"><Building2 size={26} /></div>
                    <div className="doctor-form-header-text">
                        <h3>{initialHospital ? 'Edit Hospital' : 'Add Hospital'}</h3>
                        <p>Enter hospital details, facilities and availability.</p>
                    </div>
                    <button type="button" className="medical-modal-close" onClick={onClose} aria-label="Close"><X size={20} /></button>
                </div>

                <form onSubmit={handleSubmit} className="doctor-form hospital-form-grid">
                    <div className="hospital-form-main">
                        <div className="doctor-form-section">
                            <div className="doctor-form-section-title"><IdCard size={16} /> Basic Information</div>
                            <div className="doctor-form-row">
                                <div className="medical-form-group">
                                    <label>Hospital Name <span className="required">*</span></label>
                                    <input type="text" value={form.name} onChange={(e) => update({ name: e.target.value })} placeholder="Enter hospital name" required />
                                </div>
                                <div className="medical-form-group">
                                    <label>Type <span className="required">*</span></label>
                                    <div className="medical-filter" style={{ width: '100%' }}>
                                        <select className="medical-filter-select doctor-form-select" value={form.type} onChange={(e) => update({ type: e.target.value })} required>
                                            <option value="">Select hospital type</option>
                                            {typeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                                        </select>
                                        <ChevronDown size={14} className="medical-filter-chevron" aria-hidden="true" />
                                    </div>
                                </div>
                            </div>
                            <div className="doctor-form-row">
                                <div className="medical-form-group">
                                    <label>Contact Person (Optional)</label>
                                    <div className="doctor-form-icon-field">
                                        <User size={15} className="doctor-form-icon" />
                                        <input type="text" value={form.contactPerson} onChange={(e) => update({ contactPerson: e.target.value })} placeholder="Enter contact person name" />
                                    </div>
                                </div>
                                <div className="medical-form-group">
                                    <label>Designation (Optional)</label>
                                    <div className="doctor-form-icon-field">
                                        <Briefcase size={15} className="doctor-form-icon" />
                                        <input type="text" value={form.designation} onChange={(e) => update({ designation: e.target.value })} placeholder="Enter designation" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="doctor-form-section">
                            <div className="doctor-form-section-title"><MapPin size={16} /> Location &amp; Contact</div>
                            <div className="doctor-form-row">
                                <div className="medical-form-group" style={{ flexBasis: '100%' }}>
                                    <label>Complete Address <span className="required">*</span></label>
                                    <textarea rows={2} maxLength={250} value={form.address} onChange={(e) => update({ address: e.target.value })} placeholder="Enter complete address" required />
                                    <div className="doctor-form-char-count">{form.address.length}/250</div>
                                </div>
                            </div>
                            <div className="doctor-form-row">
                                <div className="medical-form-group">
                                    <label>Area / City <span className="required">*</span></label>
                                    <div className="medical-filter" style={{ width: '100%' }}>
                                        <select className="medical-filter-select doctor-form-select" value={form.city} onChange={(e) => update({ city: e.target.value })} required>
                                            {cityOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                        <ChevronDown size={14} className="medical-filter-chevron" aria-hidden="true" />
                                    </div>
                                </div>
                                <div className="medical-form-group">
                                    <label>Phone Number <span className="required">*</span></label>
                                    <div className="doctor-form-icon-field">
                                        <Phone size={15} className="doctor-form-icon" />
                                        <input type="tel" value={form.contactNumber} onChange={(e) => update({ contactNumber: e.target.value })} placeholder="Enter phone number" required />
                                    </div>
                                </div>
                            </div>
                            <div className="doctor-form-row">
                                <div className="medical-form-group">
                                    <label>Email (Optional)</label>
                                    <div className="doctor-form-icon-field">
                                        <Mail size={15} className="doctor-form-icon" />
                                        <input type="email" value={form.email} onChange={(e) => update({ email: e.target.value })} placeholder="Enter email address" />
                                    </div>
                                </div>
                                <div className="medical-form-group">
                                    <label>Website (Optional)</label>
                                    <div className="doctor-form-icon-field">
                                        <Globe size={15} className="doctor-form-icon" />
                                        <input type="text" value={form.website} onChange={(e) => update({ website: e.target.value })} placeholder="Enter website URL" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="doctor-form-section">
                            <div className="doctor-form-section-title"><ClipboardList size={16} /> Facilities &amp; Specialties</div>
                            <div className="doctor-form-row" style={{ display: 'block' }}>
                                <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 600, color: '#4b5567', marginBottom: 8 }}>
                                    Available Facilities <span className="required">*</span>
                                </label>
                                <div className="hospital-checkbox-grid">
                                    {FACILITY_OPTIONS.map((facility) => (
                                        <label key={facility} className="hospital-checkbox-item">
                                            <input type="checkbox" checked={form.facilities.includes(facility)} onChange={() => toggleFacility(facility)} />
                                            {facility}
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div className="doctor-form-row" style={{ display: 'block' }}>
                                <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 600, color: '#4b5567', marginBottom: 8 }}>
                                    Specialties / Departments Available <span className="required">*</span>
                                </label>
                                <div className="hospital-checkbox-grid">
                                    {HOSPITAL_SPECIALTY_OPTIONS.map((specialty) => (
                                        <label key={specialty} className="hospital-checkbox-item">
                                            <input type="checkbox" checked={form.specialties.includes(specialty)} onChange={() => toggleSpecialty(specialty)} />
                                            {specialty}
                                        </label>
                                    ))}
                                </div>
                                <small className="medical-form-hint">e.g. General Medicine, Pediatrics, Orthopedics, Cardiology, etc.</small>
                            </div>
                        </div>

                        <div className="doctor-form-section">
                            <div className="doctor-form-section-title"><Calendar size={16} /> Days &amp; Timings</div>
                            <div className="doctor-form-row" style={{ display: 'block' }}>
                                <div className="doctor-form-days-label"><Calendar size={13} /> Open on Days <span className="required">*</span></div>
                                <div className="doctor-form-days">
                                    {DAYS_OF_WEEK.map((day) => (
                                        <button key={day} type="button" className={`doctor-form-day-pill ${form.daysAvailable.includes(day) ? 'active' : ''}`} onClick={() => toggleDay(day)}>
                                            {day}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="doctor-form-row hospital-timings-row">
                                <div className="medical-form-group">
                                    <label>Opening Time {!form.open24Hours && <span className="required">*</span>}</label>
                                    <div className="medical-filter" style={{ width: '100%' }}>
                                        <Clock size={13} className="medical-filter-chevron doctor-form-select-icon-left" aria-hidden="true" />
                                        <select className="medical-filter-select doctor-form-select doctor-form-select-with-left-icon" value={form.timingsFrom} onChange={(e) => update({ timingsFrom: e.target.value })} disabled={form.open24Hours}>
                                            <option value="">Select time</option>
                                            {TIME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                                        </select>
                                        <ChevronDown size={14} className="medical-filter-chevron" aria-hidden="true" />
                                    </div>
                                </div>
                                <div className="medical-form-group">
                                    <label>Closing Time {!form.open24Hours && <span className="required">*</span>}</label>
                                    <div className="medical-filter" style={{ width: '100%' }}>
                                        <Clock size={13} className="medical-filter-chevron doctor-form-select-icon-left" aria-hidden="true" />
                                        <select className="medical-filter-select doctor-form-select doctor-form-select-with-left-icon" value={form.timingsTo} onChange={(e) => update({ timingsTo: e.target.value })} disabled={form.open24Hours}>
                                            <option value="">Select time</option>
                                            {TIME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                                        </select>
                                        <ChevronDown size={14} className="medical-filter-chevron" aria-hidden="true" />
                                    </div>
                                </div>
                                <div className="hospital-toggle-group">
                                    <span>Open 24 Hours</span>
                                    <label className="hospital-toggle">
                                        <input type="checkbox" checked={form.open24Hours} onChange={(e) => update({ open24Hours: e.target.checked })} />
                                        <span className="hospital-toggle-track"><span className="hospital-toggle-thumb" /></span>
                                        <span className="hospital-toggle-label">{form.open24Hours ? 'Yes' : 'No'}</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="doctor-form-section">
                            <div className="doctor-form-section-title"><FileText size={16} /> Additional Information <span className="doctor-form-optional">(Optional)</span></div>
                            <div className="doctor-form-row">
                                <div className="medical-form-group" style={{ flexBasis: '100%' }}>
                                    <textarea rows={2} maxLength={500} value={form.additionalInfo} onChange={(e) => update({ additionalInfo: e.target.value })} placeholder="Any other important details (e.g. parking, child-friendly, nearby landmarks etc.)" />
                                    <div className="doctor-form-char-count">{form.additionalInfo.length}/500</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="hospital-form-side">
                        <div className="doctor-form-section">
                            <div className="doctor-form-section-title"><ImagePlus size={16} /> Upload Images <span className="doctor-form-optional">(Optional)</span></div>
                            <div className="doctor-form-row" style={{ display: 'block' }}>
                                <p className="medical-form-hint" style={{ marginBottom: 8 }}>Upload hospital photo, entrance, or facilities.</p>
                                <label
                                    className="hospital-dropzone"
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={(e) => { e.preventDefault(); addImageFiles(e.dataTransfer.files); }}
                                >
                                    <ImagePlus size={22} />
                                    <span>Click to upload or drag and drop</span>
                                    <small>JPG, PNG (Max 5MB each)</small>
                                    <input type="file" accept="image/*" multiple hidden onChange={(e) => addImageFiles(e.target.files)} />
                                </label>
                                <div className="hospital-thumbnails">
                                    {existingImages.map((img) => (
                                        <div key={img._id || img.fileUrl} className="hospital-thumbnail">
                                            <img src={img.fileUrl} alt={img.fileName || 'Hospital'} />
                                        </div>
                                    ))}
                                    {newImages.map((file, i) => (
                                        <div key={i} className="hospital-thumbnail">
                                            <img src={URL.createObjectURL(file)} alt={file.name} />
                                            <button type="button" className="hospital-thumbnail-remove" onClick={() => removeNewImage(i)} aria-label="Remove image"><X size={11} /></button>
                                        </div>
                                    ))}
                                    {existingImages.length + newImages.length < 6 && (
                                        <label className="hospital-thumbnail hospital-thumbnail-add">
                                            <Plus size={16} />
                                            <input type="file" accept="image/*" multiple hidden onChange={(e) => addImageFiles(e.target.files)} />
                                        </label>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="doctor-form-section">
                            <div className="doctor-form-section-title"><Stethoscope size={16} /> Doctors <span className="doctor-form-optional">(Optional)</span></div>
                            <div className="doctor-form-row" style={{ display: 'block' }}>
                                <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 600, color: '#4b5567', marginBottom: 8 }}>
                                    Doctors Available at this Hospital
                                </label>
                                <Select
                                    classNamePrefix="doctor-multiselect"
                                    isMulti
                                    options={doctorSelectOptions}
                                    value={selectedDoctorOptions}
                                    onChange={(selected) => update({ doctors: (selected || []).map((o) => o.value) })}
                                    placeholder="Search and select doctors"
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    components={{ Option: CheckboxOption }}
                                    styles={{
                                        control: (base, state) => ({
                                            ...base,
                                            minHeight: '38px',
                                            borderRadius: 8,
                                            borderColor: state.isFocused ? '#6d7cf2' : '#d7e4fb',
                                            boxShadow: 'none',
                                            fontSize: 13,
                                        }),
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        menu: (base) => ({ ...base, zIndex: 9999 }),
                                    }}
                                    menuPortalTarget={document.body}
                                />
                                <small className="medical-form-hint">Search by doctor name and check the ones available here.</small>
                            </div>
                        </div>

                        <div className="doctor-form-section">
                            <div className="doctor-form-section-title"><Eye size={16} /> Summary Preview</div>
                            <div className="hospital-summary-preview">
                                <div className="hospital-summary-photo">
                                    {previewImage ? <img src={previewImage} alt="Preview" /> : <Building2 size={28} />}
                                </div>
                                <div className="hospital-summary-rows">
                                    <div><span>Hospital Name</span><strong>{form.name || '-'}</strong></div>
                                    <div><span>Type</span><strong>{form.type || '-'}</strong></div>
                                    <div><span>Address</span><strong>{form.address || '-'}</strong></div>
                                    <div><span>Area / City</span><strong>{form.city || '-'}</strong></div>
                                    <div><span>Phone</span><strong>{form.contactNumber || '-'}</strong></div>
                                    <div><span>Email</span><strong>{form.email || '-'}</strong></div>
                                    <div><span>Website</span><strong>{form.website || '-'}</strong></div>
                                    <div><span>Facilities</span><strong>{form.facilities.join(', ') || '-'}</strong></div>
                                    <div><span>Specialties</span><strong>{form.specialties.join(', ') || '-'}</strong></div>
                                    <div><span>Doctors</span><strong>{selectedDoctorOptions.map((o) => o.label).join(', ') || '-'}</strong></div>
                                    <div>
                                        <span>Days &amp; Timings</span>
                                        <strong>
                                            {form.daysAvailable.length > 0 ? formatDayRange(form.daysAvailable) : '-'}
                                            {form.open24Hours ? ' · Open 24 Hours' : (form.timingsFrom ? ` · ${form.timingsFrom} - ${form.timingsTo || ''}` : '')}
                                        </strong>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="medical-form-footer hospital-form-footer">
                        <button type="button" className="ghost" onClick={onClose} disabled={saving}>Cancel</button>
                        <button type="submit" className="doctor-form-save" disabled={saving}>
                            <Save size={15} /> {saving ? 'Saving...' : 'Save Hospital'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// Read-only detail view (the "View Details" button).
function HospitalDetailsModal({ hospital, onClose }) {
    return (
        <div className="medical-modal-overlay" style={{ zIndex: 60 }}>
            <div className="medical-modal medical-view-modal">
                <div className="medical-modal-header">
                    <h3>{hospital.name}</h3>
                    <button type="button" className="medical-modal-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
                </div>
                <div className="medical-view-body">
                    {hospital.images?.[0]?.fileUrl && (
                        <img src={hospital.images[0].fileUrl} alt={hospital.name} style={{ width: '100%', borderRadius: 10, marginBottom: 14 }} />
                    )}
                    <div className="medical-view-grid">
                        <div><span>Type</span><strong>{hospital.type || '-'}</strong></div>
                        <div><span>Area / City</span><strong>{hospital.city || '-'}</strong></div>
                        <div><span>Phone</span><strong>{hospital.contactNumber || '-'}</strong></div>
                        <div><span>Contact Person</span><strong>{[hospital.contactPerson, hospital.designation].filter(Boolean).join(' · ') || '-'}</strong></div>
                    </div>
                    <div className="medical-view-block">
                        <span>Address</span>
                        <p>{hospital.address || '-'}</p>
                    </div>
                    <div className="medical-view-block">
                        <span>Facilities</span>
                        <p>{(hospital.facilities || []).join(', ') || '-'}</p>
                    </div>
                    <div className="medical-view-block">
                        <span>Specialties / Departments</span>
                        <p>{hospital.specialties || '-'}</p>
                    </div>
                    <div className="medical-view-block">
                        <span>Doctors</span>
                        <p>{(hospital.doctors || []).map((d) => d.name).join(', ') || '-'}</p>
                    </div>
                    <div className="medical-view-block">
                        <span>Days &amp; Timings</span>
                        <p>
                            {formatDayRange(hospital.daysAvailable) || '-'}
                            {hospital.open24Hours ? ' · Open 24 Hours' : (hospital.timingsFrom ? ` · ${hospital.timingsFrom} - ${hospital.timingsTo || ''}` : '')}
                        </p>
                    </div>
                    {(hospital.email || hospital.website) && (
                        <div className="medical-view-block">
                            <span>Contact</span>
                            <p>{[hospital.email, hospital.website].filter(Boolean).join(' · ')}</p>
                        </div>
                    )}
                    {hospital.additionalInfo && (
                        <div className="medical-view-block">
                            <span>Additional Information</span>
                            <p>{hospital.additionalInfo}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function HospitalsTab({ canManage, onCountChange }) {
    const [hospitals, setHospitals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [areaFilter, setAreaFilter] = useState('all');
    const [facilityFilter, setFacilityFilter] = useState('all');
    const [dayFilter, setDayFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [formOpen, setFormOpen] = useState(false);
    const [editingHospital, setEditingHospital] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [viewingHospital, setViewingHospital] = useState(null);
    const [menuOpenId, setMenuOpenId] = useState(null);

    useEffect(() => {
        if (!menuOpenId) return undefined;
        const closeMenu = (e) => {
            if (!e.target.closest('.hospital-menu-wrapper')) setMenuOpenId(null);
        };
        document.addEventListener('click', closeMenu);
        return () => document.removeEventListener('click', closeMenu);
    }, [menuOpenId]);

    const fetchHospitals = async () => {
        setLoading(true);
        try {
            const res = await getAllHospitals();
            const list = res?.success ? (res.data || []) : [];
            setHospitals(list);
            onCountChange?.(list.length);
        } catch (error) {
            showToast('Failed to load hospitals', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchHospitals(); }, []);
    useEffect(() => { setCurrentPage(1); }, [search, areaFilter, facilityFilter, dayFilter]);

    const areas = useMemo(
        () => Array.from(new Set(hospitals.map((h) => h.city).filter(Boolean))).sort(),
        [hospitals]
    );
    const facilities = useMemo(
        () => Array.from(new Set(hospitals.flatMap((h) => h.facilities || []))).sort(),
        [hospitals]
    );

    const filtered = hospitals.filter((h) => {
        if (areaFilter !== 'all' && h.city !== areaFilter) return false;
        if (facilityFilter !== 'all' && !(h.facilities || []).includes(facilityFilter)) return false;
        if (dayFilter !== 'all' && !(h.daysAvailable || []).includes(dayFilter)) return false;
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return (h.name || '').toLowerCase().includes(q) || (h.city || '').toLowerCase().includes(q) || (h.address || '').toLowerCase().includes(q);
    });

    const hasActiveFilters = search || areaFilter !== 'all' || facilityFilter !== 'all' || dayFilter !== 'all';
    const clearFilters = () => { setSearch(''); setAreaFilter('all'); setFacilityFilter('all'); setDayFilter('all'); };

    const totalPages = Math.max(1, Math.ceil(filtered.length / DOCTORS_PER_PAGE));
    const pageSafe = Math.min(currentPage, totalPages);
    const paginated = filtered.slice((pageSafe - 1) * DOCTORS_PER_PAGE, pageSafe * DOCTORS_PER_PAGE);

    const handleDelete = async (id) => {
        try {
            const res = await deleteHospital(id);
            if (res?.success) {
                showToast('Hospital removed', 'success');
                setDeletingId(null);
                fetchHospitals();
            } else {
                showToast(res?.message || 'Failed to delete hospital', 'error');
            }
        } catch (error) {
            showToast(error?.response?.data?.message || 'Failed to delete hospital', 'error');
        }
    };

    return (
        <div>
            <div className="doctor-list-header">
                <div>
                    <h4>Hospitals ({hospitals.length})</h4>
                    <p>Trusted hospitals with child-friendly and general healthcare facilities.</p>
                </div>
                {canManage && (
                    <button type="button" className="medical-add-btn" onClick={() => { setEditingHospital(null); setFormOpen(true); }}>
                        <Plus size={16} /> Add Hospital
                    </button>
                )}
            </div>

            <div className="medical-bank-toolbar">
                <div className="medical-search">
                    <Search size={16} className="medical-search-icon" aria-hidden="true" />
                    <input type="text" placeholder="Search by hospital name or area..." value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <div className="medical-filter">
                    <select className="medical-filter-select" value={areaFilter} onChange={(e) => setAreaFilter(e.target.value)}>
                        <option value="all">All Areas</option>
                        {areas.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                    <ChevronDown size={14} className="medical-filter-chevron" aria-hidden="true" />
                </div>
                <div className="medical-filter">
                    <select className="medical-filter-select" value={facilityFilter} onChange={(e) => setFacilityFilter(e.target.value)}>
                        <option value="all">All Facilities</option>
                        {facilities.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                    <ChevronDown size={14} className="medical-filter-chevron" aria-hidden="true" />
                </div>
                <div className="medical-filter">
                    <select className="medical-filter-select" value={dayFilter} onChange={(e) => setDayFilter(e.target.value)}>
                        <option value="all">All Days</option>
                        {DAYS_OF_WEEK.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <ChevronDown size={14} className="medical-filter-chevron" aria-hidden="true" />
                </div>
                {hasActiveFilters && (
                    <button type="button" className="medical-clear-filters" onClick={clearFilters}>Clear Filters</button>
                )}
            </div>

            {loading ? (
                <div className="medical-empty">Loading hospitals...</div>
            ) : filtered.length === 0 ? (
                <div className="medical-empty">No hospitals match your search.</div>
            ) : (
                <div className="doctor-list-table">
                    <ListHead className="hospital-list-head" columns={['#', 'Hospital', 'Key Facilities / Specialties', 'Days & Timings', 'Contact', '']} />
                    {paginated.map((hospital, index) => {
                        const tags = [...(hospital.facilities || []), ...(hospital.specialties ? hospital.specialties.split(',').map((s) => s.trim()).filter(Boolean) : [])];
                        const dayRange = formatDayRange(hospital.daysAvailable);
                        return (
                            <div key={hospital._id} className="doctor-list-row hospital-list-row">
                                <div className="doctor-list-cell hospital-list-index">{(pageSafe - 1) * DOCTORS_PER_PAGE + index + 1}</div>
                                <div className="doctor-list-cell doctor-list-cell-identity">
                                    <div className="hospital-thumb-sm" aria-hidden="true">
                                        {hospital.images?.[0]?.fileUrl ? <img src={hospital.images[0].fileUrl} alt={hospital.name} /> : <Building2 size={18} />}
                                    </div>
                                    <div>
                                        <strong>{hospital.name}</strong>
                                        {hospital.city && <div className="doctor-list-address"><MapPin size={12} /> {hospital.city}</div>}
                                    </div>
                                </div>
                                <div className="doctor-list-cell">
                                    <div className="doctor-list-cell-label">Key Facilities / Specialties</div>
                                    <div className="hospital-tag-row">
                                        {tags.length > 0 ? tags.slice(0, 4).map((tag) => <span key={tag} className="medical-doctor-tag">{tag}</span>) : '-'}
                                    </div>
                                </div>
                                <div className="doctor-list-cell">
                                    <div className="doctor-list-cell-label">Days &amp; Timings</div>
                                    {dayRange && <div className="doctor-list-days"><Calendar size={12} /> {dayRange}</div>}
                                    <div className="doctor-list-timings">{hospital.open24Hours ? 'Open 24 Hours' : (hospital.timingsFrom ? `${hospital.timingsFrom} - ${hospital.timingsTo || ''}` : (!dayRange ? '-' : ''))}</div>
                                </div>
                                <div className="doctor-list-cell doctor-list-cell-contact">
                                    {hospital.contactNumber && <a className="doctor-list-phone" href={`tel:${hospital.contactNumber}`}><Phone size={13} /> {hospital.contactNumber}</a>}
                                    {(hospital.address || hospital.city) && (
                                        <a
                                            className="doctor-list-map"
                                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([hospital.name, hospital.address, hospital.city].filter(Boolean).join(', '))}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <MapPin size={13} /> View on Map
                                        </a>
                                    )}
                                </div>
                                <div className="doctor-list-cell doctor-list-cell-actions">
                                    <button type="button" className="doctor-list-view-btn" onClick={() => setViewingHospital(hospital)}>View Details</button>
                                    {canManage && (
                                        <div className="hospital-menu-wrapper">
                                            <button type="button" className="hospital-menu-btn" onClick={() => setMenuOpenId(menuOpenId === hospital._id ? null : hospital._id)} aria-label="More actions">
                                                <MoreVertical size={15} />
                                            </button>
                                            {menuOpenId === hospital._id && (
                                                <div className="hospital-menu-dropdown">
                                                    <button type="button" onClick={() => { setEditingHospital(hospital); setFormOpen(true); setMenuOpenId(null); }}><Pencil size={13} /> Edit</button>
                                                    <button type="button" className="danger" onClick={() => { setDeletingId(hospital._id); setMenuOpenId(null); }}><Trash2 size={13} /> Delete</button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {filtered.length > 0 && (
                <div className="medical-footer">
                    <div className="medical-footer-info">
                        {`Showing ${(pageSafe - 1) * DOCTORS_PER_PAGE + 1} to ${Math.min(pageSafe * DOCTORS_PER_PAGE, filtered.length)} of ${filtered.length} hospitals`}
                    </div>
                    {totalPages > 1 && (
                        <div className="medical-pagination">
                            <button disabled={pageSafe === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>&lsaquo;</button>
                            {Array.from({ length: totalPages }).map((_, i) => (
                                <button key={i} className={pageSafe === i + 1 ? 'active' : ''} onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                            ))}
                            <button disabled={pageSafe === totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}>&rsaquo;</button>
                        </div>
                    )}
                </div>
            )}

            {formOpen && (
                <HospitalFormModal
                    initialHospital={editingHospital}
                    onClose={() => { setFormOpen(false); setEditingHospital(null); }}
                    onSaved={fetchHospitals}
                />
            )}

            {viewingHospital && (
                <HospitalDetailsModal hospital={viewingHospital} onClose={() => setViewingHospital(null)} />
            )}

            {deletingId && (
                <div className="medical-modal-overlay" style={{ zIndex: 60 }}>
                    <div className="medical-modal medical-confirm-modal">
                        <h3>Delete hospital?</h3>
                        <p>This removes the hospital from the shared data bank.</p>
                        <div className="medical-confirm-actions">
                            <button type="button" className="ghost" onClick={() => setDeletingId(null)}>Cancel</button>
                            <button type="button" className="danger" onClick={() => handleDelete(deletingId)}>Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const emptyDiagnosticForm = {
    name: '', address: '', city: 'Pune', contactNumber: '', email: '',
    daysAvailable: [], timingsFrom: '', timingsTo: '',
    testsAvailable: [], testCharges: [], additionalInfo: '', doctor: '',
};

function DiagnosticCenterFormModal({ initialCenter, onClose, onSaved }) {
    const [form, setForm] = useState(initialCenter
        ? {
            name: initialCenter.name || '',
            address: initialCenter.address || '',
            city: initialCenter.city || 'Pune',
            contactNumber: initialCenter.contactNumber || '',
            email: initialCenter.email || '',
            daysAvailable: initialCenter.daysAvailable || [],
            timingsFrom: initialCenter.timingsFrom || '',
            timingsTo: initialCenter.timingsTo || '',
            testsAvailable: initialCenter.testsAvailable || [],
            testCharges: initialCenter.testCharges || [],
            additionalInfo: initialCenter.additionalInfo || '',
            doctor: initialCenter.doctor
                ? (typeof initialCenter.doctor === 'object' ? initialCenter.doctor._id : initialCenter.doctor)
                : '',
        }
        : emptyDiagnosticForm);
    const [existingMedia] = useState(initialCenter?.media || []);
    const [newMedia, setNewMedia] = useState([]);
    const [testInput, setTestInput] = useState('');
    const [saving, setSaving] = useState(false);
    const [doctorOptions, setDoctorOptions] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const res = await getAllDoctors();
                setDoctorOptions(res?.success ? (res.data || []) : []);
            } catch (error) {
                // the doctor list is a convenience field — saving the center still works without it
            }
        })();
    }, []);

    const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

    const cityOptions = form.city && !CITY_OPTIONS.includes(form.city)
        ? [form.city, ...CITY_OPTIONS]
        : CITY_OPTIONS;

    const doctorSelectOptions = useMemo(() => (
        doctorOptions.map((d) => ({ value: d._id, label: d.specialty ? `${d.name} — ${d.specialty}` : d.name }))
    ), [doctorOptions]);
    const selectedDoctorOption = form.doctor
        ? doctorSelectOptions.find((o) => o.value === form.doctor) || null
        : null;

    const toggleDay = (day) => {
        update({
            daysAvailable: form.daysAvailable.includes(day)
                ? form.daysAvailable.filter((d) => d !== day)
                : [...form.daysAvailable, day],
        });
    };

    const addTest = () => {
        const value = testInput.trim();
        if (!value) return;
        if (!form.testsAvailable.includes(value)) {
            update({
                testsAvailable: [...form.testsAvailable, value],
                testCharges: [...form.testCharges, { name: value, charge: '' }],
            });
        }
        setTestInput('');
    };
    const removeTest = (name) => {
        update({
            testsAvailable: form.testsAvailable.filter((t) => t !== name),
            testCharges: form.testCharges.filter((c) => c.name !== name),
        });
    };
    const updateCharge = (name, charge) => {
        update({ testCharges: form.testCharges.map((c) => (c.name === name ? { ...c, charge } : c)) });
    };

    const addMediaFiles = (fileList) => {
        const files = Array.from(fileList || []).filter((f) => f.type.startsWith('image/') || f.type === 'application/pdf');
        if (files.length === 0) return;
        setNewMedia((prev) => [...prev, ...files].slice(0, 6 - existingMedia.length));
    };
    const removeNewMedia = (index) => setNewMedia((prev) => prev.filter((_, i) => i !== index));

    const handleSubmit = async (e) => {
        e.preventDefault();
        const name = form.name.trim();
        if (!name || name.length < 3) { showToast('Center name must be at least 3 characters', 'error'); return; }
        if (!form.address.trim()) { showToast('Location / Address is required', 'error'); return; }
        if (!form.city) { showToast('Please select an area / city', 'error'); return; }
        if (!form.contactNumber.trim()) { showToast('Contact number is required', 'error'); return; }
        if (form.daysAvailable.length === 0) { showToast('Please select at least one open day', 'error'); return; }
        if (!form.timingsFrom || !form.timingsTo) { showToast('Please select opening and closing timings', 'error'); return; }
        if (form.testsAvailable.length === 0) { showToast('Please add at least one test available', 'error'); return; }

        setSaving(true);
        try {
            const fd = new FormData();
            fd.append('name', name);
            fd.append('address', form.address.trim());
            fd.append('city', form.city);
            fd.append('contactNumber', form.contactNumber.trim());
            fd.append('email', form.email.trim());
            fd.append('daysAvailable', JSON.stringify(form.daysAvailable));
            fd.append('timingsFrom', form.timingsFrom);
            fd.append('timingsTo', form.timingsTo);
            fd.append('testsAvailable', JSON.stringify(form.testsAvailable));
            fd.append('testCharges', JSON.stringify(form.testCharges.map((c) => ({ ...c, charge: c.charge === '' ? undefined : Number(c.charge) }))));
            fd.append('additionalInfo', form.additionalInfo.trim());
            fd.append('doctor', form.doctor);
            newMedia.forEach((file) => fd.append('media', file));

            const response = initialCenter ? await updateDiagnosticCenter(initialCenter._id, fd) : await createDiagnosticCenter(fd);
            if (response?.success) {
                showToast(initialCenter ? 'Diagnostic center updated' : 'Diagnostic center added to data bank', 'success');
                onSaved();
                onClose();
            } else {
                showToast(response?.message || 'Failed to save diagnostic center', 'error');
            }
        } catch (error) {
            showToast(error?.response?.data?.message || 'Failed to save diagnostic center', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="medical-modal-overlay" style={{ zIndex: 60 }}>
            <div className="medical-modal doctor-form-modal hospital-form-modal">
                <div className="doctor-form-header">
                    <div className="doctor-form-header-icon diag-header-icon" aria-hidden="true"><FlaskConical size={26} /></div>
                    <div className="doctor-form-header-text">
                        <h3>{initialCenter ? 'Edit Diagnostic Center' : 'Add Diagnostic Center'}</h3>
                        <p>Enter diagnostic center details and available tests.</p>
                    </div>
                    <button type="button" className="medical-modal-close" onClick={onClose} aria-label="Close"><X size={20} /></button>
                </div>

                <form onSubmit={handleSubmit} className="doctor-form hospital-form-grid">
                    <div className="hospital-form-main">
                        <div className="doctor-form-section">
                            <div className="doctor-form-section-title"><IdCard size={16} /> Basic Information</div>
                            <div className="doctor-form-row">
                                <div className="medical-form-group" style={{ flexBasis: '100%' }}>
                                    <label>Center Name <span className="required">*</span></label>
                                    <input type="text" value={form.name} onChange={(e) => update({ name: e.target.value })} placeholder="Enter center name" required />
                                </div>
                            </div>
                            <div className="doctor-form-row">
                                <div className="medical-form-group" style={{ flexBasis: '100%' }}>
                                    <label>Location / Address <span className="required">*</span></label>
                                    <textarea rows={2} maxLength={250} value={form.address} onChange={(e) => update({ address: e.target.value })} placeholder="Enter complete address" required />
                                    <div className="doctor-form-char-count">{form.address.length}/250</div>
                                </div>
                            </div>
                            <div className="doctor-form-row">
                                <div className="medical-form-group">
                                    <label>Area / City <span className="required">*</span></label>
                                    <div className="medical-filter" style={{ width: '100%' }}>
                                        <select className="medical-filter-select doctor-form-select" value={form.city} onChange={(e) => update({ city: e.target.value })} required>
                                            {cityOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                        <ChevronDown size={14} className="medical-filter-chevron" aria-hidden="true" />
                                    </div>
                                </div>
                                <div className="medical-form-group">
                                    <label>Contact Number <span className="required">*</span></label>
                                    <div className="doctor-form-icon-field">
                                        <Phone size={15} className="doctor-form-icon" />
                                        <input type="tel" value={form.contactNumber} onChange={(e) => update({ contactNumber: e.target.value })} placeholder="Enter phone number" required />
                                    </div>
                                </div>
                            </div>
                            <div className="doctor-form-row">
                                <div className="medical-form-group">
                                    <label>Email (Optional)</label>
                                    <div className="doctor-form-icon-field">
                                        <Mail size={15} className="doctor-form-icon" />
                                        <input type="email" value={form.email} onChange={(e) => update({ email: e.target.value })} placeholder="Enter email address" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="doctor-form-section">
                            <div className="doctor-form-section-title"><Calendar size={16} /> Open on Days &amp; Timings <span className="required">*</span></div>
                            <div className="doctor-form-row" style={{ display: 'block' }}>
                                <div className="doctor-form-days-label"><Calendar size={13} /> Available Days</div>
                                <div className="doctor-form-days">
                                    {DAYS_OF_WEEK.map((day) => (
                                        <button key={day} type="button" className={`doctor-form-day-pill ${form.daysAvailable.includes(day) ? 'active' : ''}`} onClick={() => toggleDay(day)}>
                                            {day}
                                        </button>
                                    ))}
                                </div>
                                <div className="doctor-form-timings">
                                    <div className="medical-form-group">
                                        <label>From <span className="required">*</span></label>
                                        <div className="medical-filter" style={{ width: '100%' }}>
                                            <Clock size={13} className="medical-filter-chevron doctor-form-select-icon-left" aria-hidden="true" />
                                            <select className="medical-filter-select doctor-form-select doctor-form-select-with-left-icon" value={form.timingsFrom} onChange={(e) => update({ timingsFrom: e.target.value })}>
                                                <option value="">Select time</option>
                                                {TIME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                                            </select>
                                            <ChevronDown size={14} className="medical-filter-chevron" aria-hidden="true" />
                                        </div>
                                    </div>
                                    <div className="medical-form-group">
                                        <label>To <span className="required">*</span></label>
                                        <div className="medical-filter" style={{ width: '100%' }}>
                                            <Clock size={13} className="medical-filter-chevron doctor-form-select-icon-left" aria-hidden="true" />
                                            <select className="medical-filter-select doctor-form-select doctor-form-select-with-left-icon" value={form.timingsTo} onChange={(e) => update({ timingsTo: e.target.value })}>
                                                <option value="">Select time</option>
                                                {TIME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                                            </select>
                                            <ChevronDown size={14} className="medical-filter-chevron" aria-hidden="true" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="doctor-form-section">
                            <div className="doctor-form-section-title"><FileText size={16} /> Additional Information <span className="doctor-form-optional">(Optional)</span></div>
                            <div className="doctor-form-row">
                                <div className="medical-form-group" style={{ flexBasis: '100%' }}>
                                    <textarea rows={2} maxLength={250} value={form.additionalInfo} onChange={(e) => update({ additionalInfo: e.target.value })} placeholder="Any other important details (e.g. home sample collection, report time etc.)" />
                                    <div className="doctor-form-char-count">{form.additionalInfo.length}/250</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="hospital-form-side">
                        <div className="doctor-form-section">
                            <div className="doctor-form-section-title"><TestTube size={16} /> Tests Available <span className="required">*</span></div>
                            <div className="doctor-form-row" style={{ display: 'block' }}>
                                <p className="medical-form-hint" style={{ marginBottom: 8 }}>Add all types of tests available at this center.</p>
                                <div className="diag-tag-box">
                                    {form.testsAvailable.map((test) => (
                                        <span key={test} className="diag-tag-chip">
                                            {test}
                                            <button type="button" onClick={() => removeTest(test)} aria-label={`Remove ${test}`}><X size={12} /></button>
                                        </span>
                                    ))}
                                    <input
                                        type="text"
                                        value={testInput}
                                        onChange={(e) => setTestInput(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTest(); } }}
                                        placeholder={form.testsAvailable.length === 0 ? 'Type and press Enter to add test' : ''}
                                    />
                                </div>
                                <small className="medical-form-hint">e.g. Blood Test, X-ray, MRI, CT Scan, Pathology etc.</small>
                            </div>
                        </div>

                        {form.testCharges.length > 0 && (
                            <div className="doctor-form-section">
                                <div className="doctor-form-section-title"><IndianRupee size={16} /> Test Charges <span className="doctor-form-optional">(Optional)</span></div>
                                <div className="doctor-form-row" style={{ display: 'block' }}>
                                    <p className="medical-form-hint" style={{ marginBottom: 8 }}>Add approximate charges for common tests.</p>
                                    <div className="diag-charges-list">
                                        {form.testCharges.map((charge) => (
                                            <div key={charge.name} className="diag-charge-row">
                                                <span>{charge.name}</span>
                                                <div className="doctor-form-icon-field">
                                                    <IndianRupee size={13} className="doctor-form-icon" />
                                                    <input type="number" min="0" value={charge.charge} onChange={(e) => updateCharge(charge.name, e.target.value)} placeholder="Enter charge" />
                                                </div>
                                                <button type="button" className="diag-charge-remove" onClick={() => removeTest(charge.name)} aria-label={`Remove ${charge.name}`}><Trash2 size={14} /></button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="doctor-form-section">
                            <div className="doctor-form-section-title"><ImagePlus size={16} /> Upload Media <span className="doctor-form-optional">(Optional)</span></div>
                            <div className="doctor-form-row" style={{ display: 'block' }}>
                                <p className="medical-form-hint" style={{ marginBottom: 8 }}>Upload photos, brochure, list of tests, visiting card etc.</p>
                                <label
                                    className="hospital-dropzone"
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={(e) => { e.preventDefault(); addMediaFiles(e.dataTransfer.files); }}
                                >
                                    <ImagePlus size={22} />
                                    <span>Click to upload or drag and drop</span>
                                    <small>You can upload multiple files (Images/PDF)</small>
                                    <small>Max file size 5MB each</small>
                                    <input type="file" accept="image/*,application/pdf" multiple hidden onChange={(e) => addMediaFiles(e.target.files)} />
                                </label>
                                <div className="hospital-thumbnails">
                                    {existingMedia.map((file) => (
                                        <div key={file._id || file.fileUrl} className="hospital-thumbnail">
                                            {file.fileType?.startsWith('image/')
                                                ? <img src={file.fileUrl} alt={file.fileName || 'Media'} />
                                                : <a href={file.fileUrl} target="_blank" rel="noopener noreferrer" className="diag-file-tile"><FileText size={18} /></a>}
                                        </div>
                                    ))}
                                    {newMedia.map((file, i) => (
                                        <div key={i} className="hospital-thumbnail">
                                            {file.type.startsWith('image/')
                                                ? <img src={URL.createObjectURL(file)} alt={file.name} />
                                                : <div className="diag-file-tile"><FileText size={18} /></div>}
                                            <button type="button" className="hospital-thumbnail-remove" onClick={() => removeNewMedia(i)} aria-label="Remove file"><X size={11} /></button>
                                        </div>
                                    ))}
                                    {existingMedia.length + newMedia.length < 6 && (
                                        <label className="hospital-thumbnail hospital-thumbnail-add">
                                            <Plus size={16} />
                                            <input type="file" accept="image/*,application/pdf" multiple hidden onChange={(e) => addMediaFiles(e.target.files)} />
                                        </label>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="doctor-form-section">
                            <div className="doctor-form-section-title"><Stethoscope size={16} /> Doctor <span className="doctor-form-optional">(Optional)</span></div>
                            <div className="doctor-form-row" style={{ display: 'block' }}>
                                <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 600, color: '#4b5567', marginBottom: 8 }}>
                                    Doctor Available at this Center
                                </label>
                                <Select
                                    classNamePrefix="doctor-hospital-select"
                                    options={doctorSelectOptions}
                                    value={selectedDoctorOption}
                                    onChange={(option) => update({ doctor: option ? option.value : '' })}
                                    placeholder="Search and select a doctor"
                                    isClearable
                                    styles={{
                                        control: (base, state) => ({
                                            ...base,
                                            minHeight: '38px',
                                            borderRadius: 8,
                                            borderColor: state.isFocused ? '#6d7cf2' : '#d7e4fb',
                                            boxShadow: 'none',
                                            fontSize: 13,
                                        }),
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        menu: (base) => ({ ...base, zIndex: 9999 }),
                                    }}
                                    menuPortalTarget={document.body}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="medical-form-footer hospital-form-footer">
                        <button type="button" className="ghost" onClick={onClose} disabled={saving}>Cancel</button>
                        <button type="submit" className="doctor-form-save" disabled={saving}>
                            <Save size={15} /> {saving ? 'Saving...' : 'Save Diagnostic Center'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// Read-only detail view (the "View Details" button).
function DiagnosticCenterDetailsModal({ center, onClose }) {
    return (
        <div className="medical-modal-overlay" style={{ zIndex: 60 }}>
            <div className="medical-modal medical-view-modal">
                <div className="medical-modal-header">
                    <h3>{center.name}</h3>
                    <button type="button" className="medical-modal-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
                </div>
                <div className="medical-view-body">
                    {center.media?.[0]?.fileUrl && center.media[0].fileType?.startsWith('image/') && (
                        <img src={center.media[0].fileUrl} alt={center.name} style={{ width: '100%', borderRadius: 10, marginBottom: 14 }} />
                    )}
                    <div className="medical-view-grid">
                        <div><span>Area / City</span><strong>{center.city || '-'}</strong></div>
                        <div><span>Phone</span><strong>{center.contactNumber || '-'}</strong></div>
                        <div><span>Email</span><strong>{center.email || '-'}</strong></div>
                        <div>
                            <span>Days &amp; Timings</span>
                            <strong>{formatDayRange(center.daysAvailable) || '-'}{center.timingsFrom ? ` · ${center.timingsFrom} - ${center.timingsTo || ''}` : ''}</strong>
                        </div>
                    </div>
                    <div className="medical-view-block">
                        <span>Address</span>
                        <p>{center.address || '-'}</p>
                    </div>
                    <div className="medical-view-block">
                        <span>Tests Available</span>
                        <p>{(center.testsAvailable || []).join(', ') || '-'}</p>
                    </div>
                    <div className="medical-view-block">
                        <span>Doctor</span>
                        <p>{center.doctor?.name || '-'}</p>
                    </div>
                    {(center.testCharges || []).some((c) => c.charge) && (
                        <div className="medical-view-block">
                            <span>Test Charges</span>
                            <p>{center.testCharges.filter((c) => c.charge).map((c) => `${c.name}: ₹${c.charge}`).join(', ')}</p>
                        </div>
                    )}
                    {center.additionalInfo && (
                        <div className="medical-view-block">
                            <span>Additional Information</span>
                            <p>{center.additionalInfo}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function DiagnosticCentersTab({ canManage, onCountChange }) {
    const [centers, setCenters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [areaFilter, setAreaFilter] = useState('all');
    const [testFilter, setTestFilter] = useState('all');
    const [dayFilter, setDayFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [formOpen, setFormOpen] = useState(false);
    const [editingCenter, setEditingCenter] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [viewingCenter, setViewingCenter] = useState(null);
    const [menuOpenId, setMenuOpenId] = useState(null);

    const fetchCenters = async () => {
        setLoading(true);
        try {
            const res = await getAllDiagnosticCenters();
            const list = res?.success ? (res.data || []) : [];
            setCenters(list);
            onCountChange?.(list.length);
        } catch (error) {
            showToast('Failed to load diagnostic centers', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchCenters(); }, []);
    useEffect(() => { setCurrentPage(1); }, [search, areaFilter, testFilter, dayFilter]);
    useEffect(() => {
        if (!menuOpenId) return undefined;
        const closeMenu = (e) => {
            if (!e.target.closest('.hospital-menu-wrapper')) setMenuOpenId(null);
        };
        document.addEventListener('click', closeMenu);
        return () => document.removeEventListener('click', closeMenu);
    }, [menuOpenId]);

    const areas = useMemo(
        () => Array.from(new Set(centers.map((c) => c.city).filter(Boolean))).sort(),
        [centers]
    );
    const tests = useMemo(
        () => Array.from(new Set(centers.flatMap((c) => c.testsAvailable || []))).sort(),
        [centers]
    );

    const filtered = centers.filter((c) => {
        if (areaFilter !== 'all' && c.city !== areaFilter) return false;
        if (testFilter !== 'all' && !(c.testsAvailable || []).includes(testFilter)) return false;
        if (dayFilter !== 'all' && !(c.daysAvailable || []).includes(dayFilter)) return false;
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return (c.name || '').toLowerCase().includes(q) || (c.city || '').toLowerCase().includes(q) || (c.address || '').toLowerCase().includes(q);
    });

    const hasActiveFilters = search || areaFilter !== 'all' || testFilter !== 'all' || dayFilter !== 'all';
    const clearFilters = () => { setSearch(''); setAreaFilter('all'); setTestFilter('all'); setDayFilter('all'); };

    const totalPages = Math.max(1, Math.ceil(filtered.length / DOCTORS_PER_PAGE));
    const pageSafe = Math.min(currentPage, totalPages);
    const paginated = filtered.slice((pageSafe - 1) * DOCTORS_PER_PAGE, pageSafe * DOCTORS_PER_PAGE);

    const handleDelete = async (id) => {
        try {
            const res = await deleteDiagnosticCenter(id);
            if (res?.success) {
                showToast('Diagnostic center removed', 'success');
                setDeletingId(null);
                fetchCenters();
            } else {
                showToast(res?.message || 'Failed to delete diagnostic center', 'error');
            }
        } catch (error) {
            showToast(error?.response?.data?.message || 'Failed to delete diagnostic center', 'error');
        }
    };

    return (
        <div>
            <div className="doctor-list-header">
                <div>
                    <h4>Diagnostic Centers ({centers.length})</h4>
                    <p>Find trusted diagnostic centers and available tests.</p>
                </div>
                {canManage && (
                    <button type="button" className="medical-add-btn" onClick={() => { setEditingCenter(null); setFormOpen(true); }}>
                        <Plus size={16} /> Add Diagnostic Center
                    </button>
                )}
            </div>

            <div className="medical-bank-toolbar">
                <div className="medical-search">
                    <Search size={16} className="medical-search-icon" aria-hidden="true" />
                    <input type="text" placeholder="Search by center name or area..." value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <div className="medical-filter">
                    <select className="medical-filter-select" value={areaFilter} onChange={(e) => setAreaFilter(e.target.value)}>
                        <option value="all">All Areas</option>
                        {areas.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                    <ChevronDown size={14} className="medical-filter-chevron" aria-hidden="true" />
                </div>
                <div className="medical-filter">
                    <select className="medical-filter-select" value={testFilter} onChange={(e) => setTestFilter(e.target.value)}>
                        <option value="all">All Tests</option>
                        {tests.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <ChevronDown size={14} className="medical-filter-chevron" aria-hidden="true" />
                </div>
                <div className="medical-filter">
                    <select className="medical-filter-select" value={dayFilter} onChange={(e) => setDayFilter(e.target.value)}>
                        <option value="all">All Days</option>
                        {DAYS_OF_WEEK.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <ChevronDown size={14} className="medical-filter-chevron" aria-hidden="true" />
                </div>
                {hasActiveFilters && (
                    <button type="button" className="medical-clear-filters" onClick={clearFilters}>Clear Filters</button>
                )}
            </div>

            {loading ? (
                <div className="medical-empty">Loading diagnostic centers...</div>
            ) : filtered.length === 0 ? (
                <div className="medical-empty">No diagnostic centers match your search.</div>
            ) : (
                <div className="doctor-list-table">
                    <ListHead className="hospital-list-head" columns={['#', 'Diagnostic Center', 'Tests Available', 'Days & Timings', 'Contact', '']} />
                    {paginated.map((center, index) => {
                        const dayRange = formatDayRange(center.daysAvailable);
                        const mapUrl = (center.address || center.city)
                            ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([center.name, center.address, center.city].filter(Boolean).join(', '))}`
                            : null;
                        return (
                            <div key={center._id} className="doctor-list-row hospital-list-row">
                                <div className="doctor-list-cell hospital-list-index">{(pageSafe - 1) * DOCTORS_PER_PAGE + index + 1}</div>
                                <div className="doctor-list-cell doctor-list-cell-identity">
                                    <div className="hospital-thumb-sm" aria-hidden="true">
                                        {center.media?.[0]?.fileUrl && center.media[0].fileType?.startsWith('image/')
                                            ? <img src={center.media[0].fileUrl} alt={center.name} />
                                            : <FlaskConical size={18} />}
                                    </div>
                                    <div>
                                        <strong>{center.name}</strong>
                                        {center.city && <div className="doctor-list-address"><MapPin size={12} /> {center.city}</div>}
                                    </div>
                                </div>
                                <div className="doctor-list-cell">
                                    <div className="doctor-list-cell-label">Tests Available</div>
                                    <div className="hospital-tag-row">
                                        {(center.testsAvailable || []).length > 0 ? center.testsAvailable.slice(0, 4).map((t) => <span key={t} className="medical-doctor-tag">{t}</span>) : '-'}
                                    </div>
                                </div>
                                <div className="doctor-list-cell">
                                    <div className="doctor-list-cell-label">Days &amp; Timings</div>
                                    {dayRange && <div className="doctor-list-days"><Calendar size={12} /> {dayRange}</div>}
                                    <div className="doctor-list-timings">{center.timingsFrom ? `${center.timingsFrom} - ${center.timingsTo || ''}` : (!dayRange ? '-' : '')}</div>
                                </div>
                                <div className="doctor-list-cell doctor-list-cell-contact">
                                    {center.contactNumber && <a className="doctor-list-phone" href={`tel:${center.contactNumber}`}><Phone size={13} /> {center.contactNumber}</a>}
                                    {mapUrl && <a className="doctor-list-map" href={mapUrl} target="_blank" rel="noopener noreferrer"><MapPin size={13} /> View on Map</a>}
                                </div>
                                <div className="doctor-list-cell doctor-list-cell-actions">
                                    <button type="button" className="doctor-list-view-btn" onClick={() => setViewingCenter(center)}>View Details</button>
                                    {canManage && (
                                        <div className="hospital-menu-wrapper">
                                            <button type="button" className="hospital-menu-btn" onClick={() => setMenuOpenId(menuOpenId === center._id ? null : center._id)} aria-label="More actions">
                                                <MoreVertical size={15} />
                                            </button>
                                            {menuOpenId === center._id && (
                                                <div className="hospital-menu-dropdown">
                                                    <button type="button" onClick={() => { setEditingCenter(center); setFormOpen(true); setMenuOpenId(null); }}><Pencil size={13} /> Edit</button>
                                                    <button type="button" className="danger" onClick={() => { setDeletingId(center._id); setMenuOpenId(null); }}><Trash2 size={13} /> Delete</button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {filtered.length > 0 && (
                <div className="medical-footer">
                    <div className="medical-footer-info">
                        {`Showing ${(pageSafe - 1) * DOCTORS_PER_PAGE + 1} to ${Math.min(pageSafe * DOCTORS_PER_PAGE, filtered.length)} of ${filtered.length} diagnostic centers`}
                    </div>
                    {totalPages > 1 && (
                        <div className="medical-pagination">
                            <button disabled={pageSafe === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>&lsaquo;</button>
                            {Array.from({ length: totalPages }).map((_, i) => (
                                <button key={i} className={pageSafe === i + 1 ? 'active' : ''} onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                            ))}
                            <button disabled={pageSafe === totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}>&rsaquo;</button>
                        </div>
                    )}
                </div>
            )}

            {formOpen && (
                <DiagnosticCenterFormModal
                    initialCenter={editingCenter}
                    onClose={() => { setFormOpen(false); setEditingCenter(null); }}
                    onSaved={fetchCenters}
                />
            )}

            {viewingCenter && (
                <DiagnosticCenterDetailsModal center={viewingCenter} onClose={() => setViewingCenter(null)} />
            )}

            {deletingId && (
                <div className="medical-modal-overlay" style={{ zIndex: 60 }}>
                    <div className="medical-modal medical-confirm-modal">
                        <h3>Delete diagnostic center?</h3>
                        <p>This removes the diagnostic center from the shared data bank.</p>
                        <div className="medical-confirm-actions">
                            <button type="button" className="ghost" onClick={() => setDeletingId(null)}>Cancel</button>
                            <button type="button" className="danger" onClick={() => handleDelete(deletingId)}>Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// `embedded` renders just the directory (no Back button / full-page shell),
// for the Medical Incharge dashboard's in-page "Medical" tab.
export default function DoctorBank({ embedded = false }) {
    const navigate = useNavigate();
    const { canCreate } = usePermission();
    // Coaches only ever get view access to the Doctor Bank, even if their
    // role is otherwise granted Create on "Medical Check-in" — this does
    // not affect any other role's existing Add/Edit/Delete access.
    // Medical Incharge and admin can always add/edit doctors, hospitals and
    // diagnostic centers (the backend routes allow those roles too).
    const role = localStorage.getItem('role');
    const isCoach = role === 'coach';
    const canManage = (canCreate('Medical Check-in') || role === 'medical-incharge' || role === 'admin') && !isCoach;

    const [activeTab, setActiveTab] = useState('doctors');
    const [counts, setCounts] = useState({ doctors: 0, hospitals: 0, diagnostics: 0 });

    useEffect(() => {
        (async () => {
            try {
                const [doctorsRes, hospitalsRes, diagnosticsRes] = await Promise.all([
                    getAllDoctors(), getAllHospitals(), getAllDiagnosticCenters(),
                ]);
                setCounts({
                    doctors: doctorsRes?.success ? (doctorsRes.data || []).length : 0,
                    hospitals: hospitalsRes?.success ? (hospitalsRes.data || []).length : 0,
                    diagnostics: diagnosticsRes?.success ? (diagnosticsRes.data || []).length : 0,
                });
            } catch (error) {
                // counts are cosmetic — the tabs themselves handle their own load errors
            }
        })();
    }, []);

    return (
        <div className={embedded ? 'doctor-bank-embedded' : 'doctor-bank-page'}>
            {!embedded && (
                <button
                    type="button"
                    className="doctor-bank-back-btn"
                    onClick={() => navigate('/dashboard', { state: { activeTab: 'medical' } })}
                >
                    <ArrowLeft size={14} /> Back
                </button>
            )}

            <div className="doctor-bank-page-body">
                <div className="medical-bank-tabs">
                    <button type="button" className={`medical-bank-tab ${activeTab === 'doctors' ? 'active' : ''}`} onClick={() => setActiveTab('doctors')}>
                        <span className="medical-bank-tab-icon" aria-hidden="true"><Stethoscope size={20} /></span>
                        <div>
                            <div className="medical-bank-tab-title">Doctors <span>{counts.doctors}</span></div>
                            <div className="medical-bank-tab-desc">Find doctors by name, specialty or area.</div>
                        </div>
                    </button>
                    <button type="button" className={`medical-bank-tab ${activeTab === 'hospitals' ? 'active' : ''}`} onClick={() => setActiveTab('hospitals')}>
                        <span className="medical-bank-tab-icon" aria-hidden="true"><Building2 size={20} /></span>
                        <div>
                            <div className="medical-bank-tab-title">Hospitals <span>{counts.hospitals}</span></div>
                            <div className="medical-bank-tab-desc">Find hospitals and their facilities.</div>
                        </div>
                    </button>
                    <button type="button" className={`medical-bank-tab ${activeTab === 'diagnostics' ? 'active' : ''}`} onClick={() => setActiveTab('diagnostics')}>
                        <span className="medical-bank-tab-icon tone-yellow" aria-hidden="true"><FlaskConical size={20} /></span>
                        <div>
                            <div className="medical-bank-tab-title">Diagnostic Centers <span>{counts.diagnostics}</span></div>
                            <div className="medical-bank-tab-desc">Find diagnostic centers and available tests.</div>
                        </div>
                    </button>
                </div>

                <div className="medical-bank-body">
                    {activeTab === 'doctors' && (
                        <DoctorsTab canManage={canManage} onCountChange={(n) => setCounts((c) => ({ ...c, doctors: n }))} />
                    )}
                    {activeTab === 'hospitals' && (
                        <HospitalsTab canManage={canManage} onCountChange={(n) => setCounts((c) => ({ ...c, hospitals: n }))} />
                    )}
                    {activeTab === 'diagnostics' && (
                        <DiagnosticCentersTab canManage={canManage} onCountChange={(n) => setCounts((c) => ({ ...c, diagnostics: n }))} />
                    )}
                </div>
            </div>
        </div>
    );
}
